import React, { useState, useEffect } from 'react';
import { featureRequestsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Lightbulb, 
  Plus, 
  ThumbsUp, 
  ThumbsDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'ui', label: 'User Interface' },
  { value: 'integration', label: 'Integration' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'trucking', label: 'Trucking' },
  { value: 'other', label: 'Other' },
];

const STATUSES = [
  { value: 'submitted', label: 'Submitted', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
  { value: 'under_review', label: 'Under Review', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'planned', label: 'Planned', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'declined', label: 'Declined', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

const getStatusConfig = (status) => {
  return STATUSES.find(s => s.value === status) || STATUSES[0];
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed': return <CheckCircle className="h-4 w-4" />;
    case 'declined': return <XCircle className="h-4 w-4" />;
    case 'in_progress': return <Settings className="h-4 w-4 animate-spin" />;
    case 'planned': return <Clock className="h-4 w-4" />;
    case 'under_review': return <AlertCircle className="h-4 w-4" />;
    default: return <Lightbulb className="h-4 w-4" />;
  }
};

const FeatureRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('votes');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general'
  });

  useEffect(() => {
    loadRequests();
  }, [filterStatus, filterCategory, sortBy]);

  const loadRequests = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort_by: sortBy };
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      
      const response = await featureRequestsAPI.list(params);
      setRequests(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load feature requests:', error);
      toast.error('Failed to load feature requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await featureRequestsAPI.create(formData);
      toast.success('Feature request submitted!');
      setSubmitDialogOpen(false);
      setFormData({ title: '', description: '', category: 'general' });
      loadRequests();
    } catch (error) {
      console.error('Failed to submit feature request:', error);
      toast.error('Failed to submit feature request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (requestId, vote) => {
    try {
      const response = await featureRequestsAPI.vote(requestId, vote);
      // Update the request in the list
      setRequests(prev => prev.map(r => 
        r.id === requestId ? response.data : r
      ));
    } catch (error) {
      console.error('Failed to vote:', error);
      toast.error('Failed to submit vote');
    }
  };

  const handleAdminUpdate = async () => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      await featureRequestsAPI.update(selectedRequest.id, {
        status: selectedRequest.status,
        priority: selectedRequest.priority,
        admin_notes: selectedRequest.admin_notes
      });
      toast.success('Feature request updated');
      setEditDialogOpen(false);
      setSelectedRequest(null);
      loadRequests();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update feature request');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (request) => {
    setSelectedRequest({ ...request });
    setEditDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="feature-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Lightbulb className="h-8 w-8 text-yellow-400" />
            Feature Requests
          </h1>
          <p className="text-muted-foreground mt-1">
            Submit and vote on feature ideas
          </p>
        </div>
        <Button onClick={() => setSubmitDialogOpen(true)} data-testid="submit-feature-btn">
          <Plus className="h-4 w-4 mr-2" />
          Submit Request
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filters:</span>
            </div>
            
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">Sort:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="votes">Most Votes</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : requests.length === 0 ? (
        <Card className="border-border/50 bg-card/50">
          <CardContent className="p-12 text-center">
            <Lightbulb className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No feature requests yet</h3>
            <p className="text-muted-foreground mb-4">Be the first to submit a feature idea!</p>
            <Button onClick={() => setSubmitDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Submit Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const statusConfig = getStatusConfig(request.status);
            const netVotes = request.upvotes - request.downvotes;
            
            return (
              <Card 
                key={request.id} 
                className="border-border/50 bg-card/50 hover:bg-card/80 transition-colors"
                data-testid={`feature-request-${request.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Voting */}
                    <div className="flex flex-col items-center gap-1 min-w-[60px]">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 ${request.user_vote === 1 ? 'text-emerald-400 bg-emerald-500/20' : ''}`}
                        onClick={() => handleVote(request.id, request.user_vote === 1 ? 0 : 1)}
                        data-testid={`upvote-${request.id}`}
                      >
                        <ArrowUp className="h-5 w-5" />
                      </Button>
                      <span className={`text-lg font-bold ${netVotes > 0 ? 'text-emerald-400' : netVotes < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        {netVotes}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 ${request.user_vote === -1 ? 'text-red-400 bg-red-500/20' : ''}`}
                        onClick={() => handleVote(request.id, request.user_vote === -1 ? 0 : -1)}
                        data-testid={`downvote-${request.id}`}
                      >
                        <ArrowDown className="h-5 w-5" />
                      </Button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-lg font-semibold">{request.title}</h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant="outline" className={statusConfig.color}>
                            {getStatusIcon(request.status)}
                            <span className="ml-1">{statusConfig.label}</span>
                          </Badge>
                          {user?.role === 'admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(request)}
                              data-testid={`edit-${request.id}`}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-muted-foreground mb-3 whitespace-pre-wrap">
                        {request.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {CATEGORIES.find(c => c.value === request.category)?.label || request.category}
                        </Badge>
                        <span>by {request.submitted_by_name}</span>
                        <span>•</span>
                        <span>{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</span>
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="h-3 w-3" /> {request.upvotes}
                        </span>
                        <span className="flex items-center gap-1">
                          <ThumbsDown className="h-3 w-3" /> {request.downvotes}
                        </span>
                      </div>

                      {request.admin_notes && (
                        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                          <p className="text-sm text-blue-400">
                            <strong>Admin Note:</strong> {request.admin_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => loadRequests(pagination.page - 1)}
            disabled={!pagination.has_prev}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <Button
            variant="outline"
            onClick={() => loadRequests(pagination.page + 1)}
            disabled={!pagination.has_next}
          >
            Next
          </Button>
        </div>
      )}

      {/* Submit Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-yellow-400" />
              Submit Feature Request
            </DialogTitle>
            <DialogDescription>
              Share your idea to help improve the system
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Brief summary of your idea"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="feature-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger data-testid="feature-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe your feature idea in detail. What problem does it solve? How would it work?"
                rows={5}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="feature-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-feature-confirm-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Feature Request</DialogTitle>
            <DialogDescription>
              Update status and add admin notes
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-background/50 rounded-lg">
                <h4 className="font-medium mb-1">{selectedRequest.title}</h4>
                <p className="text-sm text-muted-foreground">{selectedRequest.description}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={selectedRequest.status} 
                  onValueChange={(v) => setSelectedRequest({ ...selectedRequest, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={selectedRequest.priority || 'none'} 
                  onValueChange={(v) => setSelectedRequest({ ...selectedRequest, priority: v === 'none' ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No priority set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No priority</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Textarea
                  placeholder="Add notes visible to all users..."
                  rows={3}
                  value={selectedRequest.admin_notes || ''}
                  onChange={(e) => setSelectedRequest({ ...selectedRequest, admin_notes: e.target.value })}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdminUpdate} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeatureRequests;
